import React, { Component } from 'react';
import { connect, useSelector } from 'react-redux';
import ScrollReactTable from 'components/shared/ScrollReactTable/ScrollReactTable';
import {
  patchDeviceParameters,
  updateSingleDevicePagePatchValues,
  clearSingleDevicePagePatchValues,
  updateStatus
} from 'redux/actions';
import classnames from 'classnames';
import ConsoleTile from 'components/shared/ConsoleTile/ConsoleTile';
import './DeviceConfigTable.css';
import { DebounceInput } from 'react-debounce-input';
import { getLocalTimeZone, isObjectEmpty } from 'common/utils/helpers';
import { Status } from 'common/utils/constants';
import { ErrorBox } from 'components/shared/ErrorBox/ErrorBox';
import { TableStatusTag } from 'components/features/TableStatusTag/TableStatusTag';
import SvgAF from 'components/shared/SvgAF/SvgAF';
import { ReactComponent as IconSpinnerSmall } from 'imgs/icons/windows/icon_spinner_small.svg';
import Modal from 'components/shared/Modal/Modal';
import ConfigHistory from 'components/features/ConfigHistory/ConfigHistory';
import { IconLoader } from 'common/IconLoader';
import { PerfectScrollbar } from 'components/shared/ReactPerfectScrollbar';
import { enableCommissionActionForGa2 } from '../ProvisionModal/utils';

const moment = require('moment-timezone');

const tempSchema = [
  {
    access: 'read-write',
    description: 'empty',
    name: 'PM_update_interval',
    type: 'INTEGER'
  },
  {
    access: 'read-only',
    description: 'empty',
    name: 'adminState',
    type: 'ENUM',
    enum: ['idle', 'lock', 'running']
  },
  {
    access: 'read-only',
    description: 'empty',
    name: 'operationState',
    type: 'STRING'
  },
  {
    access: 'read-only',
    description: 'empty',
    name: 'beamTableCRC',
    type: 'STRING'
  },
  {
    access: 'read-only',
    description: 'empty',
    name: 'lastResetReason',
    type: 'STRING'
  },
  {
    access: 'read-only',
    description: 'empty',
    name: 'sysCurTime',
    type: 'STRING'
  }
];

const StatusStrings = {
  SUCCESS: 'Success',
  FAIL: 'Failed',
  PENDING: 'Pending',
  NONE: ''
};

const OnlineStatus = {
  ONLINE: 0,
  OFFLINE: 1,
  UNKNOWN: 2
};

const CONFIG_HISTORY_MODAL_WIDTH = 1254;

/* Status Cell broken out of device config table class as a first step towards eliminating unnecessary renders.
   TODO - find a way to remove dependencies from device config table state, i.e. get device ID from store and
          keep the patch status in the store so device config table can remove those pieces and prevent rerenders*/
const StatusCell = ({ id, parameter, status, row, dispatch, deviceHistoryCopy }) => {
  const patchValues = useSelector((store) => store.ui.singleDevicePage.patchValues);

  let loadingIcon = <div />;
  let invalidDiv = <div className={'DeviceConfigTable_Input_TypeErrorText'}>value must be integer</div>;

  if (status === StatusStrings.PENDING) {
    loadingIcon = <IconSpinnerSmall className="DeviceConfigTable_Status_LoadingIcon" />;
  } else if (status === StatusStrings.SUCCESS) {
    loadingIcon = (
      <TableStatusTag
        status={'Success'}
        onClickHandler={() => {
          console.debug('undo clicked,', 'param:', parameter);
          try {
            dispatch(
              updateSingleDevicePagePatchValues({
                [parameter]:
                  deviceHistoryCopy[id]?.parameters[parameter] ||
                  deviceHistoryCopy[id]?.properties?.reported?.[parameter]
              })
            );
          } catch (error) {
            console.error(error);
          }
        }}
      />
    );
  } else if (status === StatusStrings.FAIL) {
    loadingIcon = (
      <TableStatusTag
        status={'Failed'}
        onClickHandler={() => {
          console.debug('undo clicked, parameter:', parameter);
        }}
      />
    );
  } else if (
    row.original.type === 'INTEGER' &&
    patchValues[parameter] != null &&
    patchValues[parameter] !== '' &&
    isNaN(parseInt(patchValues[parameter]))
  ) {
    loadingIcon = invalidDiv;
  }

  return <div className="DeviceConfigTable_Status_Div">{loadingIcon}</div>;
};

class DeviceConfigTable extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedInput: null, //used to maintain focus on input boxes during rerenders
      patchStatus: {}, //patch statuses get set to pending on patch, set to success if value read back after patch matches patchValue in store
      fetchError: null,
      patchError: null,
      isConfigHisotryFrameOpen: false,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight
    };
    this.renderInput = this.renderInput.bind(this);
    this.fetchErrorCallback = this.fetchErrorCallback.bind(this);
    this.patchErrorCallback = this.patchErrorCallback.bind(this);
    this.checkValidInputs = this.checkValidInputs.bind(this);
    this.selectedInput = null;
    this.inputsDisabled = false;

    //columns moved to constructor, Cells still rerender with table but Headers seem to be exempt, preventing some hover icon / tooltip weirdness
    this.columns = [
      // {
      //     Header: 'Category',
      //     accessor: 'category',
      // },
      {
        Header: () => {
          return (
            <div className="DeviceConfigTable_Header_OuterDiv">
              <div className="DeviceConfigTable_Header_Title">Parameter</div>
              <div className="DeviceConfigTable_Header_ResizerSliderIcon">
                <div className="DeviceConfigTable_Header_ResizerSliderIcon_Bar"></div>
              </div>
            </div>
          );
        },
        accessor: 'parameter',
        headerClassName: 'ParameterHeader TableHeader',
        sortable: false,
        Cell: (row) => <div className="DeviceConfigTable_cellWrapper">{row.value}</div>
        //Cell: (row) => <div style={{textAlign:"left", width:"100%"}}>{row.value}</div>
        //aggregate: (values, rows) => ''
      },
      {
        Header: () => {
          return (
            <div className="DeviceConfigTable_Header_OuterDiv">
              <div className="DeviceConfigTable_Header_Title">Type</div>
              <div className="DeviceConfigTable_Header_ResizerSliderIcon">
                <div className="DeviceConfigTable_Header_ResizerSliderIcon_Bar"></div>
              </div>
            </div>
          );
        },
        accessor: 'type',
        sortable: false,
        Cell: (row) => <div className="DeviceConfigTable_cellWrapper">{row.value}</div>
      },
      // {
      //     Header: 'Min/Max',
      //     accessor: 'minMax',
      //     aggregate: (values, rows) => ''
      // },
      {
        Header: () => {
          return (
            <div className="DeviceConfigTable_Header_OuterDiv">
              <div className="DeviceConfigTable_Header_Title">Value</div>
              <div className="DeviceConfigTable_Header_ResizerSliderIcon">
                <div className="DeviceConfigTable_Header_ResizerSliderIcon_Bar"></div>
              </div>
            </div>
          );
        },
        accessor: 'value',
        headerClassName: 'TableHeader ParameterHeader',
        sortable: false,
        Cell: (row) => (
          <div className="DeviceConfigTable_cellWrapper DeviceConfigTable_ValueDiv">
            <div className="DeviceConfigTable_ValueInnerDiv">{row.value}</div>
          </div>
        )
      },
      {
        Header: () => {
          let localTimeZone = getLocalTimeZone();

          return (
            <div className="DeviceConfigTable_Header_OuterDiv DeviceConfigTable_lastUpdatedHeader">
              <div>Last updated on &nbsp;</div>
              <div>(</div>
              <div className="DeviceConfigTable_lastUpdatedHeaderZone">{localTimeZone.abbr}</div>
              <div>)</div>
              <SvgAF
                img={IconLoader.icon_help_xs}
                hover={IconLoader.icon_help_xs_hover}
                tooltip={localTimeZone.full + ' - your local timezone'}
              />
              <div className="DeviceConfigTable_Header_ResizerSliderIcon">
                <div className="DeviceConfigTable_Header_ResizerSliderIcon_Bar"></div>
              </div>
            </div>
          );
        },
        accessor: 'lastUpdated',
        sortable: false,
        Cell: (row) => <div className="DeviceConfigTable_cellWrapper">{row.value}</div>
      },
      {
        Header: () => {
          return (
            <div className="DeviceConfigTable_Header_OuterDiv">
              <div className="DeviceConfigTable_Header_Title DeviceConfigTable_Header_NewValue">
                <div className="centerWrapper">
                  <div>New Value</div>
                </div>
              </div>
              <div className="DeviceConfigTable_Header_ResizerSliderIcon">
                <div className="DeviceConfigTable_Header_ResizerSliderIcon_Bar"></div>
              </div>
            </div>
          );
        },
        headerClassName: 'TableHeader DeviceConfigTable_newValueHeader',
        sortable: false,
        Cell: this.renderInput
      },
      {
        sortable: false,
        Header: () => {
          const deviceId = this.props.device?.id ?? this.props.device?.deviceId;
          let revert = false;
          Object.keys(this.state.patchStatus).forEach((key) => {
            /* Only show revert all if a patch succeeded*/
            if (this.state.patchStatus[key] === StatusStrings.SUCCESS) {
              revert = true;
            }
          });
          if (revert) {
            return (
              <div className="DeviceConfigTable_Status_Div">
                <TableStatusTag
                  status={'Revert all'}
                  onClickHandler={() => {
                    let patchValues = {};
                    Object.keys(this.state.patchStatus).forEach((key) => {
                      patchValues[key] =
                        this.props.deviceHistory[deviceId]?.parameters[key] ||
                        this.props.deviceHistory[deviceId]?.properties?.reported[key];
                    });

                    /* Uncomment this if we want to revert it at the moment, if not remove it */
                    // const lastValues = this.props.device?.parameters;

                    // this.props.dispatch(
                    //   patchDeviceParameters(deviceId, { parameters: patchValues }, lastValues, this.patchErrorCallback)
                    // );

                    this.props.dispatch(updateSingleDevicePagePatchValues(patchValues));
                  }}
                />
              </div>
            );
          } else {
            return <div className="DeviceConfigTable_Status_Div" style={{ height: '40px' }} />;
          }
        },
        Cell: (row) => (
          <StatusCell
            parameter={row.row.parameter}
            id={this.props.device.id ?? this.props.device.deviceId}
            status={this.state.patchStatus[row.row.parameter]}
            row={row}
            dispatch={this.props.dispatch}
            deviceHistoryCopy={this.props.deviceHistory}
          />
        )
      }
    ];
  }

  componentDidMount() {
    window.addEventListener('resize', this.updateDimensions);
    // this.props.dispatch(fetchDeviceParameterSchema("echo_5g_subscriber", "0.3.12"));
    this.props.dispatch(clearSingleDevicePagePatchValues());
  }

  componentDidUpdate(prevProps) {
    if (this.props.isGa2 && prevProps.device !== this.props.device) {
      this.inputsDisabled = !enableCommissionActionForGa2(this.props.device);
    }
  }

  createData() {
    if (!this.props.device) {
      this.data = [];
      return;
      //return [];
    }
    // if(this.data != null && this.data.length > 0)
    // {
    //     return;
    // }

    let data = [];
    let params = this.props.device.parameters || this.props.device.properties.reported;

    //let schema = this.props.schema
    let schema = tempSchema;
    //console.log("device parameters", params);
    let localTimeZone = moment.tz.guess();
    schema.forEach((entry) => {
      if (params[entry.name] == null) {
        /* If the value is in the schema but not on the device, something weird has happened, and we will eventually need to deal with it */
        //console.log("!!! Value in schema not found on device !!!");
      }

      let localTimeFormatted = 'N/A';

      if (params.$metadata && params.$metadata[entry.name] && params.$metadata[entry.name].$lastUpdated) {
        let utcTime = params.$metadata[entry.name].$lastUpdated;
        let localTime = moment.utc(utcTime).tz(localTimeZone);
        localTimeFormatted = localTime.format('MMM Do YYYY, hh:mm:ss a');
      }

      data.push({
        parameter: entry.name,
        value: params[entry.name],
        lastUpdated: localTimeFormatted,
        access: entry.access,
        type: entry.type,
        enum: entry.enum
      });
    });
    this.data = data;
  }

  checkDeviceStatus() {
    if (!this.props.device || !this.props.device) {
      return OnlineStatus.UNKNOWN;
    }
    if (this.props.device.connectionState === 'Connected') {
      return OnlineStatus.ONLINE;
    } else if (this.props.device.connectionState === 'Disconnected') {
      return OnlineStatus.OFFLINE;
    } else {
      return OnlineStatus.UNKNOWN;
    }
  }

  checkValidInputs() {
    let keys = Object.keys(this.props.patchValues);

    let i = 0;
    for (i; i < keys.length; i++) {
      if (this.props.patchValues[keys[i]] != null) {
        break;
      }
    }

    if (i === keys.length) {
      return false; //there are no inputs with values, therefore overall input is invalid (used to disable apply button when there are no inputs)
    }

    for (i = 0; i < this.data.length; i++) {
      if (
        this.data[i].type === 'INTEGER' &&
        this.props.patchValues[this.data[i].parameter] != null &&
        this.props.patchValues[this.data[i].parameter] !== '' &&
        isNaN(parseInt(this.props.patchValues[this.data[i].parameter]))
      ) {
        return false;
      }
    }
    return true;
  }

  renderInput(row) {
    if (row.original.access === 'read-only' || this.checkDeviceStatus() !== OnlineStatus.ONLINE) {
      return <div />;
    }

    let changed = false;
    let error = false;
    if (this.props.patchValues[row.row.parameter]) {
      changed = true;
    }
    if (this.state.patchStatus[row.row.parameter] === StatusStrings.FAIL) {
      error = true;
    }

    if (row.original.type === 'ENUM') {
      return (
        <div
          className="DeviceConfigTable_Input_Div"
          onFocus={(event) => {
            this.selectedInput = null;
          }}
        >
          <select
            className={'DeviceConfigTable_Select'}
            value={this.props.patchValues[row.row.parameter] ? this.props.patchValues[row.row.parameter] : ''}
            onChange={(event) => {
              if (event.target.value === '') {
                this.props.dispatch(updateSingleDevicePagePatchValues({ [row.row.parameter]: null }));
              } else {
                let value = event.target.value;
                this.props.dispatch(updateSingleDevicePagePatchValues({ [row.row.parameter]: value }));
              }
            }}
          >
            <option value={''}></option>
            {row.original.enum.map((val) => {
              return (
                <option key={val} value={val}>
                  {val}
                </option>
              );
            })}
          </select>
        </div>
      );
    }

    return (
      <div className="DeviceConfigTable_Input_Div">
        <div className="centerWrapper">
          <DebounceInput
            className={classnames({
              DebounceInput: true,
              'DebounceInput-changed': changed,
              'DebounceInput-error': error,
              'DebounceInput-disabled': this.inputsDisabled
            })}
            debounceTimeout={200}
            disabled={this.inputsDisabled}
            inputRef={row.index === this.selectedInput ? (input) => input && input.focus() : null}
            type="text"
            value={this.props.patchValues[row.row.parameter] ? this.props.patchValues[row.row.parameter] : ''}
            onFocus={(event) => {
              if (this.selectedInput !== row.index) {
                this.selectedInput = row.index;
                event.target.value = null;
                this.props.dispatch(updateSingleDevicePagePatchValues({ [row.row.parameter]: null }));
              }
            }}
            onBlur={(event) => {
              if (this.selectedInput === row.index) {
                this.selectedInput = null;
              }
            }}
            onChange={(event) => {
              if (event.target.value === '') {
                this.props.dispatch(updateSingleDevicePagePatchValues({ [row.row.parameter]: null }));
              } else {
                let value = event.target.value;
                this.props.dispatch(updateSingleDevicePagePatchValues({ [row.row.parameter]: value }));
              }
              this.selectedInput = row.index;
            }}
          />
        </div>
      </div>
    );
  }

  handlePatchSuccess = () => {
    this.props.refreshDeviceData && this.props.refreshDeviceData();

    const deviceId = this.props.device?.id ?? this.props.device?.deviceId;
    let newStatus = {};
    Object.keys(this.state.patchStatus).forEach((key) => {
      if (this.props.patchParamsStatus[key]) {
        console.log('SUCCESS, key:', key);
        newStatus[key] = StatusStrings.SUCCESS;
      } else {
        console.log('FAIL, key:', key, 'status object:', this.props.statuses['PATCH-PARAMS-' + deviceId]);
        newStatus[key] = StatusStrings.FAIL;
      }
    });
    this.props.dispatch(updateStatus('PATCH-' + deviceId, Status.NONE));
    this.props.dispatch(clearSingleDevicePagePatchValues());
    this.setState({ patchStatus: newStatus });
    this.props.deviceHistory[deviceId] = this.props.device;
  };

  fetchErrorCallback(message) {
    this.setState({ fetchError: message });
  }
  patchErrorCallback(message, params) {
    const newStatus = {};
    Object.keys(params.parameters).forEach((key) => {
      newStatus[key] = StatusStrings.FAIL;
    });
    this.setState({ patchError: message, patchStatus: newStatus });
  }

  updateDimensions = () => {
    this.setState({ windowWidth: window.innerWidth, windowHeight: window.innerHeight });
  };

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateDimensions);
  }

  launchConfigHistory = () => {
    const { device, getCountersStatus, dispatch } = this.props;
    let deviceId = device && device.deviceId;
    let statusName = deviceId && `GetCounters-${deviceId}`;
    if (getCountersStatus == null) dispatch(updateStatus(statusName, Status.PENDING));
    this.setState({
      isConfigHisotryFrameOpen: true
    });
  };

  render() {
    const deviceId = this.props.device?.id ?? this.props.device?.deviceId;
    let onlineStatus = this.checkDeviceStatus();
    const {
      launchConfigHistory,
      state: { isConfigHisotryFrameOpen, windowHeight }
    } = this;
    let tileHeader = this.props.device ? (
      <div className="DeviceConfigTable_TileHeader">
        <div>Configuration</div>
        <SvgAF
          img={IconLoader.icon_confighistory}
          hover={IconLoader.icon_confighistory_hover}
          tooltip={'Config History'}
          onClick={launchConfigHistory}
        />
      </div>
    ) : null;

    let configHistoryModal = null;
    if (isConfigHisotryFrameOpen) {
      configHistoryModal = (
        <Modal
          width={CONFIG_HISTORY_MODAL_WIDTH}
          height={windowHeight - 32}
          colorize={'#f8f9fa'}
          opaque={true}
          global
          close={() => this.setState({ isConfigHisotryFrameOpen: false })}
          className={'configHistoryModalContainer'}
        >
          <PerfectScrollbar>
            <ConfigHistory deviceId={this.props.deviceId} />
          </PerfectScrollbar>
        </Modal>
      );
    }

    this.createData();
    let data = this.data;
    let pageSize = 7;
    if (data) {
      if (data.length > pageSize) {
        pageSize = data.length;
      }
    } else {
      data = [];
    }

    if (data?.length === 0) {
      return (
        <ConsoleTile title="Configuration" tileHeader={tileHeader}>
          <div>Loading Device Data...</div>
        </ConsoleTile>
      );
    }

    let overlay = null;
    if (this.state.fetchError) {
      let message;
      switch (this.state.fetchError) {
        case 'unknown':
        default:
          message = (
            <div className="Global_Table_ErrorOverlay_Message">
              {' '}
              An unknown error occured while getting the parameter schema for this device. If this problem persists,
              please contact us at
              <a href="mailto:support@pivotalcommware.com"> support@pivotalcommware.com</a>
            </div>
          );
          break;
      }
      overlay = (
        <div className="Global_Table_ErrorOverlay">
          <div className="Global_Table_ErrorOverlay_Title">Error </div>
          {message}
        </div>
      );
    }

    let errorBox = null;
    if (this.state.patchError) {
      let message;
      switch (this.state.patchError) {
        case 'timeout':
          message = (
            <div className="ErrorBox_Message">
              {' '}
              The process timed out while updating the device configuration. If this problem persists, please contact us
              at
              <a href="mailto:support@pivotalcommware.com"> support@pivotalcommware.com</a>
            </div>
          );
          break;
        case 'unknown':
        default:
          message = (
            <div className="ErrorBox_Message">
              {' '}
              An unknown error occurred while updating the device configuration. If this problem persists, please
              contact us at
              <a href="mailto:support@pivotalcommware.com"> support@pivotalcommware.com</a>
            </div>
          );
          break;
      }
      errorBox = (
        <div className="DeviceConfigTable_ErrorBox_Div">
          <ErrorBox
            message={message}
            close={() => {
              this.setState({ patchError: null });
            }}
          />
        </div>
      );
    }

    return (
      <ConsoleTile title="Configuration" tileHeader={tileHeader}>
        {configHistoryModal}
        <div className="DeviceConfigTableDiv">
          <ScrollReactTable
            noDataText={'Fetching Data...'}
            showPagination={false}
            resizable={true}
            overlay={overlay}
            loading={isObjectEmpty(this.props.device)}
            className={'DeviceConfigTable Global_Table'}
            pageSize={pageSize}
            getTdProps={(state, rowInfo, column, instance) => {
              return {
                onClick: (e, handleOriginal) => {
                  if (handleOriginal) {
                    handleOriginal(); /* Suggested by documentation, makes sure that default click functions still get called if necessary*/
                  }
                }
              };
            }}
            getTrProps={(state, rowInfo, column, instance) => {
              if (rowInfo) {
                return {
                  className: classnames({
                    'highlighted-row':
                      this.props.patchValues[rowInfo.row.parameter] != null &&
                      this.props.patchValues[rowInfo.row.parameter] !== rowInfo.row.value,
                    DeviceConfigTable: true,
                    Global_Table: true
                  })
                };
              } else {
                return {};
              }
            }}
            data={data}
            columns={this.columns} //{createColumns(this.props.dispatch, this)}
            //pivotBy={["category"]}
          />
          {errorBox}
          {onlineStatus === OnlineStatus.ONLINE && errorBox == null ? (
            <div className="DeviceConfigTable_Button_Div">
              <button
                className="DeviceCongigTable_Button DeviceConfigTable_Button_Cancel Global_RoundedButton"
                disabled={
                  this.props.device == null ||
                  this.props.patchDeviceStatus === Status.PENDING ||
                  this.state.fetchError != null ||
                  this.checkValidInputs() === false
                }
                onClick={() => {
                  this.props.dispatch(clearSingleDevicePagePatchValues());
                  this.selectedInput = -1;
                }}
              >
                Cancel
              </button>
              <button
                className={'DeviceCongigTable_Button DeviceConfigTable_Button_Accept Global_RoundedButton'}
                disabled={
                  this.props.device == null ||
                  this.props.patchDeviceStatus === Status.PENDING ||
                  this.state.fetchError != null ||
                  this.checkValidInputs() === false
                }
                onClick={() => {
                  console.log('Patching Device');

                  let patchValues = { ...this.props.patchValues };
                  let lastValues = {};

                  for (let i = 0; i < this.data.length; i++) {
                    console.log('checking parameter:', this.data[i].parameter);
                    if (patchValues[this.data[i].parameter] != null && patchValues[this.data[i].parameter] !== '') {
                      console.log('parameter in PATCH. Checking type');
                      if (this.data[i].type === 'INTEGER') {
                        let value = parseInt(patchValues[this.data[i].parameter], 10);
                        if (isNaN(value)) {
                          console.log('invalid input, tbd how this is handled');
                          return;
                        }
                        console.log('parsed value:', value);
                        patchValues[this.data[i].parameter] = value;
                        lastValues = this.props.device;
                      }
                    }
                  }

                  this.props.dispatch(
                    patchDeviceParameters(
                      deviceId,
                      { parameters: patchValues },
                      lastValues,
                      this.patchErrorCallback,
                      this.handlePatchSuccess
                    )
                  );
                  let pending = {};
                  Object.keys(patchValues).forEach((key) => {
                    if (patchValues[key] != null && patchValues[key] !== '') {
                      pending[key] = StatusStrings.PENDING;
                    }
                  });
                  this.selectedInput = -1;
                  this.setState({ patchStatus: pending });
                }}
              >
                Apply
              </button>
            </div>
          ) : null}
        </div>
      </ConsoleTile>
    );
  }
}

export default connect((store) => {
  const deviceId = store.ui.singleDevicePage.selectedDevice && store.ui.singleDevicePage.selectedDevice.id;
  return {
    deviceId,
    patchValues: store.ui.singleDevicePage.patchValues,
    patchDeviceStatus: store.statuses[`PATCH-${deviceId}`],
    patchParamsStatus: store.statuses[`PATCH-PARAMS-${deviceId}`],
    deviceHistory: store.data.devicesStorage.storedDevicesDictionary,
    getCountersStatus: store.statuses[`GetCounters-${deviceId}`]
  };
})(DeviceConfigTable);
